import {
  gridPanelVoided,
  selectCanSeeContentBadges,
  selectShouldShowContentSelector,
} from 'features/layout/features/content/contentSlice';
import { selectScreenshareStreams } from 'features/layout/features/order/orderSlice';
import { GridPanelContainer } from 'features/layout/grid-panel/GridPanelContainer';
import { ExtraItem, PanelContentSelector } from 'features/layout/grid-panel/PanelContentSelector';
import {
  localScreenshareStopped,
  screenshareSelected,
  stopRemoteScreenshare,
} from 'features/screenshare/actions';
import { useBulkCloseScopeControl } from 'features/layout/grid-panel/useBulkCloseScopeControl';
import {
  selectSelectedScreenshare,
  selectShowAllScreens,
  showAllScreensChanged,
} from 'features/screenshare/screenshareSlice';
import { useCanRemoveScreenshare } from 'features/screenshare/hooks/useCanRemoveScreenshare';
import { sourceMatches } from 'features/streaming/utils';
import { UserScreenshareSource } from 'features/layout/features/order/types';
import { selectLocalUserId } from 'features/users/usersSlice';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useBreakpoints } from 'hooks/useBreakpoints';
import { useTranslation } from 'react-i18next';

import { MultiScreenshares } from './MultiScreenshares';
import { SingleScreenshare } from './SingleScreenshare';

export const ScreensharePanel = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation(['room', 'common']);

  const screenshares = useAppSelector(selectScreenshareStreams);
  const showAllScreens = useAppSelector(selectShowAllScreens);
  const { isMobile } = useBreakpoints();

  const shouldShowSelector = useAppSelector(selectShouldShowContentSelector);
  const canSeeContentBadges = useAppSelector(selectCanSeeContentBadges);
  const localUserId = useAppSelector(selectLocalUserId);
  const bulkCloseScreenshares = useBulkCloseScopeControl('screenshares');
  const canRemoveScreenshare = useCanRemoveScreenshare();

  useEffect(() => {
    if (!screenshares.length) {
      dispatch(gridPanelVoided('screenshare'));
    }
  }, [screenshares.length, dispatch]);

  const selectedScreenshare = useAppSelector(selectSelectedScreenshare);

  const activeScreenshareEntry = useMemo(
    () => screenshares.find((screenshare) => sourceMatches(screenshare, selectedScreenshare)),
    [screenshares, selectedScreenshare]
  );

  const selectScreenshare = (idx: number) => {
    const screenshare = screenshares[idx];
    if (screenshare?.feedId) {
      dispatch(
        screenshareSelected({
          feedId: screenshare.feedId,
          userId: screenshare.userId,
          update: true,
        })
      );
    }
  };

  const toggleShowAllScreens = useCallback(
    () => dispatch(showAllScreensChanged(!showAllScreens)),
    [showAllScreens, dispatch]
  );

  const extraItems = useMemo(() => {
    const items: ExtraItem[] = [];

    if (screenshares.length > 1 && !isMobile) {
      items.push({
        title: t('toolbar.show_all_screens'),
        action: toggleShowAllScreens,
        active: showAllScreens,
      });
    }

    if (bulkCloseScreenshares.enabled) {
      items.push({
        title: bulkCloseScreenshares.label,
        action: bulkCloseScreenshares.handleClick,
      });
    }

    return items;
  }, [
    t,
    screenshares.length,
    isMobile,
    toggleShowAllScreens,
    showAllScreens,
    bulkCloseScreenshares.enabled,
    bulkCloseScreenshares.label,
    bulkCloseScreenshares.handleClick,
  ]);

  const removeScreenshare = useCallback(
    (stream: UserScreenshareSource | undefined) => {
      if (!stream) {
        return;
      }
      if (stream.userId === localUserId) {
        dispatch(localScreenshareStopped());
      } else {
        dispatch(stopRemoteScreenshare(stream.userId));
      }
    },
    [dispatch, localUserId]
  );

  const renderLabel = useCallback(
    (screenshare: UserScreenshareSource | undefined, active?: boolean) => {
      if (active && showAllScreens) {
        return t('screenshare.all_screens');
      }

      return `${t('screenshare.single')} (${screenshare?.kind === 'screenshare-local' ? t('you') : screenshare?.user?.name})`;
    },
    [showAllScreens, t]
  );

  return (
    <GridPanelContainer
      sx={{ alignItems: 'center', justifyContent: 'center' }}
      id="screenshare-container"
    >
      {canSeeContentBadges && (activeScreenshareEntry || showAllScreens) && (
        <PanelContentSelector
          items={screenshares}
          activeItem={activeScreenshareEntry}
          onSelect={selectScreenshare}
          onRemoveItem={removeScreenshare}
          canRemoveItem={canRemoveScreenshare}
          renderLabel={renderLabel}
          getItemId={(screenshare) => screenshare?.feedId}
          extraItems={extraItems}
          disabled={!shouldShowSelector}
        />
      )}
      {showAllScreens ? <MultiScreenshares /> : <SingleScreenshare />}
    </GridPanelContainer>
  );
};
